import { router, usePage } from "@inertiajs/react";
import axios from "axios";
import DOMPurify from "dompurify";
import parse, { domToReact } from "html-react-parser";
import { memo, useContext, useEffect, useState } from "react";

import BB_Breadcrumbs, { BB_PageLink } from "@/Components/BB_Breadcrumbs";
import BB_Button from "@/Components/BB_Button";
import BB_CompactProductsList from "@/Components/BB_CompactProductsList";
import BB_LinkList from "@/Components/BB_LinkList";
import BB_Meta from "@/Components/BB_Meta";
import BB_ProductCell from "@/Components/BB_ProductCell";
import BB_ProductHero from "@/Components/BB_ProductHero";
import BB_ProductUpdates, { BB_Update } from "@/Components/BB_ProductUpdates";
import { DriverTourContext } from "@/ContextProviders/DriverTour";
import { FirebaseContext } from "@/ContextProviders/FirebaseProvider";
import { SizeContext } from "@/ContextProviders/SizeProvider";
import BellIcon from "@/Icons/bell.svg?react";
import ChevronLeftIcon from "@/Icons/chevron_left.svg?react";
import RemoveBellIcon from "@/Icons/removeBell.svg?react";
import SpinnerIcon from "@/Icons/spinner.svg?react";
import StarIcon from "@/Icons/stars.svg?react";
import AuthenticatedLayout from "@/Layouts/AuthenticatedLayout";
import GuestLayout from "@/Layouts/GuestLayout";
import BB_Analysis from "@/types/BB_Analysis";
import BB_Link from "@/types/BB_Link";
import BB_Paginator from "@/types/BB_Paginator";
import BB_PaginatorCursor from "@/types/BB_PaginatorCursor";
import BB_Product from "@/types/BB_Product";
import BB_User from "@/types/BB_User";
import { generateMailtoLink } from "@/Utils/MailtoGenerator";

export type ProductPageProps = {
	auth: { user: BB_User | null };

	product: BB_Product;
	product_siblings?: BB_PaginatorCursor<BB_Product>;
	product_children: BB_PaginatorCursor<BB_Product>;
	product_children_amount: number;
	parent_product: BB_Product | null;
	links: BB_Paginator<BB_Link>;
	buybox_link: BB_Link | null;

	updates: BB_PaginatorCursor<BB_Update>;
	analysis: BB_Analysis | null;
	popular_products_for_widget: BB_Product[] | null;
	relevant_products: BB_Product[] | null;
};

const ProductPage = (props: ProductPageProps) => {
	const Layout = props.auth.user ? AuthenticatedLayout : GuestLayout;

	const [children, setChildren] = useState<BB_Product[]>(props.product_children.data ?? []);
	const [childrenNextCursor, setChildrenNextCursor] = useState<string | null>(
		props.product_children.next_cursor
	);
	const [isLoadingChildren, setIsLoadingChildren] = useState(false);
	const [loadMoreChildrenAmount, setLoadMoreChildrenAmount] = useState<number>(
		props.product_children_amount
	);

	const [analysis, setAnalysis] = useState<ReturnType<typeof domToReact>>();
	const [analysisExpanded, setAnalysisExpanded] = useState(false);
	const [productDescription, setProductDescription] = useState<ReturnType<typeof domToReact>>();

	const [isFollowedAll, setIsFollowedAll] = useState<boolean>(props.product.followed ?? false);
	const [isSubscribeAllLoading, setIsSubscribeAllLoading] = useState<boolean>(false);

	const [showUnsubscribeAllButton, setShowUnsubscribeAllButton] = useState<boolean>(
		props.product.followed_any_links ?? false
	);
	const [isUnsubscribeAllLoading, setIsUnsubscribeAllLoading] = useState<boolean>(false);

	const [isAutoFollowingNewLinks, setIsAutoFollowingNewLinks] = useState<boolean>(
		props.product.auto_followed ?? false
	);
	const [isAutoFollowingNewLinksLoading, setIsAutoFollowingNewLinksLoading] =
		useState<boolean>(false);

	const [updates, setUpdates] = useState<BB_Update[]>(props.updates.data ?? []);
	const [updatesNextCursor, setUpdatesNextCursor] = useState<string | null>(
		props.updates.next_cursor
	);
	const [isLoadingUpdates, setIsLoadingUpdates] = useState(false);

	const { data, ...linksPaginatorWithoutData } = props.links || {};
	const [filteredLinks, setFilteredLinks] = useState<BB_Link[]>(data);
	const [linksPaginator, setLinksPaginator] = useState<BB_Paginator<BB_Link> | null>(
		linksPaginatorWithoutData ? { data: [], ...linksPaginatorWithoutData } : null
	);
	const [isLoadingLinks, setIsLoadingLinks] = useState<boolean>(false);
	const [loadMoreLinksAmount, setLoadMoreLinksAmount] = useState<number>(0);

	const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);

	const { startMainTour } = useContext(DriverTourContext);
	const { registerForPush } = useContext(FirebaseContext);
	const { isCompact, isMedium } = useContext(SizeContext);
	const { url } = usePage();

	useEffect(() => {
		const queryParams = new URLSearchParams(url.split("?")[1]);
		const startTutorial = queryParams.get("tutorial") === "1";
		if (startTutorial) {
			startMainTour();
		}
	}, [isCompact]);

	const getBreadcrumbPages = () => {
		const maxDepth = 5;
		const breadcrumbs: BB_PageLink[] = [];

		breadcrumbs.unshift({
			id: props.product.id,
			name: props.product.name,
			href: null
		});

		if (props.product.ancestors) {
			const productAncestorsRev = [...props.product.ancestors].reverse();
			for (const ancestor of productAncestorsRev) {
				if (isCompact || breadcrumbs.length + 2 < maxDepth) {
					breadcrumbs.unshift({
						id: ancestor.id,
						name: ancestor.name,
						href: route("p", {
							b_id: ancestor.b_id,
							slug: ancestor.slug
						})
					});
				} else {
					breadcrumbs.unshift({ name: "...", href: null });
					break;
				}
			}
		}

		if (props.product.category) {
			breadcrumbs.unshift({
				id: props.product.category.id,
				name: props.product.category.name,
				href: route("explore", {
					b_id: props.product.category.b_id,
					slug: props.product.category.slug
				})
			});

			if (props.product.category.ancestors) {
				const categoryAncestorsRev = [...props.product.category.ancestors].reverse();
				for (const ancestor of categoryAncestorsRev) {
					if (isCompact || breadcrumbs.length + 1 < maxDepth) {
						breadcrumbs.unshift({
							id: ancestor.id,
							name: ancestor.name,
							href: route("explore", {
								b_id: ancestor.b_id,
								slug: ancestor.slug
							})
						});
					} else {
						breadcrumbs.unshift({ name: "...", href: null });
						break;
					}
				}
			}
		}

		breadcrumbs.unshift({ id: "explorePage", name: "Entdecken", href: route("explore") });

		return breadcrumbs;
	};

	const calculateAmountOfLinksToLoadOnNextFetch = () => {
		if (!linksPaginator) {
			return 0;
		}

		let left = linksPaginator.total - filteredLinks.length;
		setLoadMoreLinksAmount(left > linksPaginator.per_page ? linksPaginator.per_page : left);
	};

	useEffect(() => {
		calculateAmountOfLinksToLoadOnNextFetch();

		if (props.analysis) {
			if (typeof window !== "undefined") {
				const analysisHtml = parse(DOMPurify.sanitize(props.analysis.analysis_html));
				setAnalysis(analysisHtml);
			} else {
				const analysisEl = (
					<div dangerouslySetInnerHTML={{ __html: props.analysis.analysis_html }}></div>
				);
				setAnalysis(analysisEl);
			}
		}
        if (props.product.seo_text) {
            if (typeof window !== "undefined") {
                const seoText = parse(DOMPurify.sanitize(props.product.seo_text));
                setProductDescription(seoText);
            } else {
                const seoTextEl = (
                    <div dangerouslySetInnerHTML={{ __html: props.product.seo_text }}></div>
                );
                setProductDescription(seoTextEl);
            }
        }
	}, []);

	const handleSubscribeNew = async () => {
		if (needsLogin()) {
			return;
		}

		setIsAutoFollowingNewLinksLoading(true);
		setButtonsDisabled(true);

		try {
			const response = await axios.post(
				`/api/follow-new-links/product/${props.product.id}/${isAutoFollowingNewLinks ? "unfollow" : ""}`
			);

			if (response.status === 200) {
				setIsAutoFollowingNewLinks(!isAutoFollowingNewLinks);
			}
		} catch (error) {
			console.error("[🧨] Error toggling auto-follow for new links", error);
		}

		setButtonsDisabled(false);
		setIsAutoFollowingNewLinksLoading(false);
	};

	const needsLogin = (): boolean => {
		if (props.auth.user === null) {
			// If user is not logged in, redirect to special page
			router.visit("/redirect/guests");
			return true;
		} else {
			return false;
		}
	};

	const handleSubscribleAll = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();

		if (needsLogin()) {
			return;
		}

		if (isSubscribeAllLoading) return;
		setIsSubscribeAllLoading(true);
		setButtonsDisabled(true);

		if (!isFollowedAll) registerForPush();

		try {
			const response = await axios.post(
				`/api/follow/product/${props.product.id}/${isFollowedAll ? "unfollow" : "follow"}`
			);

			if (response.status === 200) {
				const temp = filteredLinks.map((link) => {
					if (link.is_search_link) return link;
					link.followed = !isFollowedAll;
					return link;
				});

				setIsAutoFollowingNewLinks(!isFollowedAll);
				if (isFollowedAll) setShowUnsubscribeAllButton(false);
				setFilteredLinks(temp);
				setIsFollowedAll((prev) => !prev);
			} else {
				console.error("[🧨] Cannot follow/unfollow all");
			}
		} catch (error) {
			console.error("[🧨] Error following/unfollowing all", error);
		}

		setIsSubscribeAllLoading(false);
		setButtonsDisabled(false);
	};

	const handleUnsubscribleAll = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();

		// If user is not logged in, redirect to special page
		if (props.auth.user === null) {
			router.visit("/redirect/guests");
			return;
		}

		if (isSubscribeAllLoading) return;
		setIsUnsubscribeAllLoading(true);
		setButtonsDisabled(true);

		if (!isFollowedAll) registerForPush();

		try {
			const response = await axios.post(`/api/follow/product/${props.product.id}/unfollow`);

			if (response.status === 200) {
				const temp = filteredLinks.map((link) => {
					if (link.is_search_link) return link;
					link.followed = false;
					return link;
				});

				setFilteredLinks(temp);
				setIsFollowedAll(false);
				setShowUnsubscribeAllButton(false);
				setIsAutoFollowingNewLinks(false);
			} else {
				console.error("[🧨] Cannot unfollow all");
			}
		} catch (error) {
			console.error("[🧨] Error unfollowing all", error);
		}

		setIsUnsubscribeAllLoading(false);
		setButtonsDisabled(false);
	};

	const toggleFollowedState = (currentLink: BB_Link) => {
		const temp = filteredLinks.map((link) => {
			if (link.is_search_link) return link;
			if (link.id === currentLink.id) link.followed = !link.followed;
			return link;
		});
		setFilteredLinks(temp);
	};

	useEffect(() => {
		const left = props.product_children_amount - children.length;
		setLoadMoreChildrenAmount(left > 4 ? 4 : left);
	}, [props.product_children_amount, children]);

	const loadMoreChildren = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();
		if (isLoadingChildren) return;

		try {
			setIsLoadingChildren(true);

			const nextPage = `/api/get/children/${props.product.id}?cursor=${childrenNextCursor}`;
			const response = await axios.get(nextPage);

			setChildren([...children, ...response.data.data]);
			setChildrenNextCursor(response.data.next_cursor);
		} catch (error) {
			console.error("[🧨] Cannot load more descendants", error);
		}

		setIsLoadingChildren(false);
	};

	const loadMoreLinks = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();
		if (isLoadingLinks || !linksPaginator?.current_page) return;

		try {
			setIsLoadingLinks(true);
			setButtonsDisabled(true);

			const nextPage = `/api/get/links/${props.product.id}?page=${linksPaginator?.current_page + 1}`;
			const response = await axios.get(nextPage);

			const { data, ...linksPaginatorWithoutData } = response.data;
			setFilteredLinks([...filteredLinks, ...data]);
			setLinksPaginator({ data: [], ...linksPaginatorWithoutData });
		} catch (error) {
			console.error("[🧨] Cannot load more links", error);
		}

		setIsLoadingLinks(false);
		setButtonsDisabled(false);
	};

	useEffect(() => {
		calculateAmountOfLinksToLoadOnNextFetch();
	}, [filteredLinks, linksPaginator]);

	const loadMoreUpdates = async (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();
		if (isLoadingUpdates) return;

		try {
			setIsLoadingUpdates(true);

			const nextPage = `/api/get/updates/${props.product.id}?cursor=${updatesNextCursor}`;
			const response = await axios.get(nextPage);

			setUpdates([...updates, ...response.data.data]);
			setUpdatesNextCursor(response.data.next_cursor);
		} catch (error) {
			console.error("[🧨] Cannot load more updates", error);
		}

		setIsLoadingUpdates(false);
	};

	const followedAllProducts = (followed: boolean) => {
		setIsFollowedAll(followed);
	};

	const shouldShowPopularProducts =
		(props.updates && props.updates.data.length > 0) ||
		analysis !== undefined ||
		productDescription !== undefined ||
		isMedium;

	return (
		<Layout>
			<BB_Meta
				title={`${props.product.title || props.product.name} kaufen: Bei Verfügbarkeit benachrichtigen - Bestell.bar`}
				description={props.product.desc}
				url={route("p", { b_id: props.product.b_id, slug: props.product.slug }, true)}
				image={
					props.product.images && props.product.images.length > 0
						? `${props.product.images[0].url}?class=6`
						: undefined
				}
				imageWidth={
					props.product.images && props.product.images.length > 0
						? props.product.images[0].width
						: undefined
				}
				imageHeight={
					props.product.images && props.product.images.length > 0
						? props.product.images[0].height
						: undefined
				}
				imageAlt={props.product.title && `${props.product.title} Bild`}
				keywords={`${props.product.title?.replace(",", "").split(" ").join(", ")}, Verfügbarkeit, kaufen, bestellen, Benachrichtigung, Verfügbarkeitsbenachrichtigung, Preisvergleich`}
			/>

			<BB_Breadcrumbs pages={getBreadcrumbPages()} />

			<div className="bg-background-100 dark:bg-dark-background-300 rounded-none py-6 shadow-lg sm:rounded-lg">
				<BB_ProductHero
					isSubscribeAllLoading={isSubscribeAllLoading}
					handleSubscribleAll={handleSubscribleAll}
					isFollowedAll={isFollowedAll}
					product_siblings={props.product_siblings?.data}
					product={props.product}
					buybox_link={props.buybox_link}
					parent_product={props.parent_product}
					buttonsDisabled={buttonsDisabled}
				/>
			</div>

			{children && children.length > 0 && (
				<div id="childProducts" className="relative pt-4">
					<h2 className="my-4 flex flex-row items-center ps-3 text-left text-xl font-bold sm:ps-2">
						<p>Varianten</p>
					</h2>
					<div className="bg-background-100 dark:bg-dark-background-300 rounded-none px-3 pt-5 pb-5 shadow-lg sm:rounded-lg sm:px-6">
						<div
							className={
								"xs:grid-cols-2 3xl:grid-cols-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
							}>
							{children.map((product, index) => (
								<BB_ProductCell
									key={product.id}
									index={index}
									title={product.name}
									product={product}
									isCompact={true}
								/>
							))}
						</div>
						{childrenNextCursor && (
							<BB_Button
								type="secondary"
								buttonClassName="mt-4 flex-1 w-50"
								onClick={loadMoreChildren}
								icon={isLoadingChildren ? SpinnerIcon : undefined}
								iconClassName={`h-5 w-5 ${isLoadingChildren ? "pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500" : ""}`}
								title={
									isLoadingChildren
										? ""
										: `Mehr laden (${loadMoreChildrenAmount})`
								}
							/>
						)}
					</div>
				</div>
			)}

			<div className="pt-4">
				<h2 className="my-4 flex flex-row items-center ps-3 text-left text-xl font-bold sm:ps-2">
					{/*
                        <SpinnerIcon className="inline w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-primary-500" />
                    */}
					<p className="hidden md:block">Verfügbarkeiten & Preisvergleich</p>
					<p className="block md:hidden">Verfügbarkeiten</p>
				</h2>
				<div className="bg-background-100 dark:bg-dark-background-300 rounded-none pt-5 pb-5 shadow-lg sm:rounded-lg">
					<div className="border-background-200 dark:border-dark-background-200 mb-3 flex animate-none flex-row flex-wrap items-center justify-between gap-3 border-b-2 px-3 pb-5 sm:gap-5 sm:px-6">
						{isFollowedAll ? (
							<BB_Button
								type="unknown"
								id="allAlarms"
								title={
									isSubscribeAllLoading
										? ""
										: (isCompact && "Alle deaktivieren") ||
											"Alle Alarme deaktivieren"
								}
								icon={isSubscribeAllLoading ? SpinnerIcon : RemoveBellIcon}
								buttonClassName={isCompact ? "w-60" : "w-75"}
								iconClassName={
									isSubscribeAllLoading
										? "h-5 w-5 pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"
										: "h-5 w-5 mr-2 pointer-events-none"
								}
								onClick={handleSubscribleAll}
								disabled={buttonsDisabled}
							/>
						) : (
							<div className={"flex flex-wrap gap-3"}>
								<BB_Button
									type="notify"
									id="allAlarms"
									title={
										isSubscribeAllLoading
											? ""
											: (isCompact && "Alle aktivieren") ||
												"Alle Alarme aktivieren"
									}
									icon={isSubscribeAllLoading ? SpinnerIcon : BellIcon}
									iconClassName={
										isSubscribeAllLoading
											? "h-5 w-5 pointer-events-none animate-spin dark:text-gray-600 fill-primary-500"
											: "h-5 w-5 mr-2 pointer-events-none"
									}
									buttonClassName={isCompact ? "w-60" : "w-75"}
									onClick={handleSubscribleAll}
									disabled={buttonsDisabled}
								/>
								{showUnsubscribeAllButton && (
									<BB_Button
										type="unknown"
										title={
											isUnsubscribeAllLoading
												? ""
												: (isCompact && "Alle deaktivieren") ||
													"Alle Alarme deaktivieren"
										}
										icon={
											isUnsubscribeAllLoading ? SpinnerIcon : RemoveBellIcon
										}
										buttonClassName={isCompact ? "w-60" : "w-75"}
										iconClassName={
											isUnsubscribeAllLoading
												? "h-5 w-5 pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"
												: "h-5 w-5 mr-2 pointer-events-none"
										}
										onClick={handleUnsubscribleAll}
										disabled={buttonsDisabled}
									/>
								)}
							</div>
						)}

						<label
							id="followNewLinks"
							className="mr-auto flex cursor-pointer items-center select-none">
							<input
								type="checkbox"
								id="checkAll"
								name="checkAll"
								className="dark:bg-dark-background-400 text-primary-500 ring-primary-500 h-4 w-4 rounded-sm border-gray-300"

								checked={isAutoFollowingNewLinks ?? false}
								onChange={handleSubscribeNew}
								disabled={isAutoFollowingNewLinksLoading || buttonsDisabled}
							/>
							<span className="ml-2 text-sm hyphens-auto">
								{`Automatisch neuen ${props.product_children.data.length > 0 ? " Varianten & " : ""}Links folgen`}
							</span>
						</label>
						<BB_Button
							type="secondary"
							title="Fehler melden"
							onClick={generateMailtoLink(
								`Fehler: ${props.product.title}`,
								"Hey Bestell.bar-Team,\n\nHier gibt es einen Fehler:\n\nBeste Grüße"
							)}
							buttonClassName="hidden lg:block cursor-pointer ml-auto"
						/>
					</div>

					<div className={"px-3 sm:px-6"}>
						<BB_LinkList
							links={filteredLinks}
							product={props.product}
							registerForPush={registerForPush}
							isSubscribeAllLoading={isSubscribeAllLoading}
							followedAllProducts={followedAllProducts}
							buttonsDisabled={buttonsDisabled}
							setButtonsDisabled={setButtonsDisabled}
							setShowUnsubscribeAllButton={setShowUnsubscribeAllButton}
							toggleFollowedState={toggleFollowedState}
							needsLogin={needsLogin}
						/>

						<div className="mt-4 flex">
							{linksPaginator?.next_page_url && (
								<BB_Button
									type="secondary"
									buttonClassName="me-auto w-50"
									onClick={loadMoreLinks}
									icon={isLoadingLinks ? SpinnerIcon : undefined}
									iconClassName={`h-5 w-5 ${isLoadingLinks ? "pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500" : ""}`}
									title={
										isLoadingLinks ? "" : `Mehr laden (${loadMoreLinksAmount})`
									}
									disabled={buttonsDisabled}
								/>
							)}
							{!linksPaginator && props.parent_product && (
								<BB_Button
									onClick={route("p", {
										b_id: props.parent_product.b_id,
										slug: props.parent_product.slug
									})}
									type="secondary"
									icon={ChevronLeftIcon}
									iconClassName="h-5 w-5 mr-2 pointer-events-none"
									title={
										isCompact
											? "Alle anzeigen"
											: `Alle "${props.parent_product.name}" anzeigen`
									}
									buttonClassName="sm:ml-0 cursor-pointer me-auto"
								/>
							)}
							<div className="grow"></div>
							<BB_Button
								type="secondary"
								title="Fehler melden"
								onClick={generateMailtoLink(
									`Fehler: ${props.product.title}`,
									"Hey Bestell.bar-Team,\n\nHier gibt es einen Fehler:\n\nBeste Grüße"
								)}
								buttonClassName="cursor-pointer me-auto lg:hidden sm:mr-0"
							/>
						</div>

						<div className="text-background-500 dark:text-background-400 mt-4 text-xs hyphens-auto select-none">
							Disclaimer: Sämtliche Produktinformationen, insbesondere der Preis,
							können veraltet oder gänzlich falsch sein. Bitte beim Händler
							kontrollieren! Bei Klick auf einen Link, bspw. von Amazon, sowie
							anschließendem Kauf erhalten wir ggf. eine Provision vom Händler. Dies
							hat keinen Einfluss auf den Preis.
						</div>
					</div>
				</div>
			</div>

			<div className="flex flex-col gap-0 lg:flex-row lg:gap-6">
				<div className="flex flex-col lg:w-2/3">
					{props.updates && props.updates.data.length > 0 && (
						<div id="updates" className="mt-4">
							<h2
								className="my-4 ps-3 text-left text-xl font-bold sm:ps-2"
								id="updatesTitle">
								Updates
							</h2>
							<div className="bg-background-100 dark:bg-dark-background-300 relative rounded-none px-3 py-6 shadow-lg sm:rounded-lg sm:px-6">
								<BB_ProductUpdates updates={updates} />
								{updatesNextCursor && (
									<div className="from-background-100 dark:from-dark-background-300 via-background-100/80 dark:via-dark-background-300/80 absolute bottom-6 left-0 flex h-30 w-full items-end justify-center bg-gradient-to-t to-transparent">
										<BB_Button
											type="secondary"
											buttonClassName="mb-4 mt-3 mx-auto w-50"
											onClick={loadMoreUpdates}
											icon={isLoadingUpdates ? SpinnerIcon : undefined}
											iconClassName={`h-5 w-5 ${isLoadingUpdates ? "pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500" : ""}`}
											title={isLoadingUpdates ? "" : "Mehr laden"}
										/>
									</div>
								)}
							</div>
						</div>
					)}

					{analysis && (
						<div id="analysis" className="mt-4 list-disc">
							<h2
								className="my-4 flex items-center ps-3 text-left text-xl font-bold sm:ps-2"
								id="analysisTitle">
								<StarIcon className="mr-2 h-5 w-5 text-[#0ea5e9]" />
								KI-Analyse zur Verfügbarkeit
							</h2>
							<div className="is-html bg-background-100 dark:bg-dark-background-300 border-background-100 dark:border-dark-background-300 relative flex flex-col items-start border p-6 hyphens-auto sm:rounded-lg">
								<div
									className={`${
										analysisExpanded ? "line-clamp-none" : "line-clamp-15"
									}`}>
									{analysis}
									{!analysisExpanded && (
										<div className="from-background-100 dark:from-dark-background-300 via-background-100/80 dark:via-dark-background-300/80 absolute bottom-6 left-0 flex h-30 w-full items-end justify-center bg-gradient-to-t to-transparent">
											<BB_Button
												type="secondary"
												title="Mehr anzeigen"
												buttonClassName="w-40"
												onClick={() => setAnalysisExpanded(true)}
											/>
										</div>
									)}
								</div>
								{analysisExpanded && (
									<BB_Button
										type="secondary"
										title="Weniger anzeigen"
										buttonClassName="mt-3 mx-auto w-40"
										onClick={() => setAnalysisExpanded(false)}
									/>
								)}
							</div>
						</div>
					)}

					{productDescription && (
                        <div className="list-disc mt-4">
                            <h2 className="text-left text-xl font-bold my-4 ps-3 sm:ps-2 flex items-center">
								Produktbeschreibung
                            </h2>
                            <div className="is-html bg-background-100 dark:bg-dark-background-300 border-background-100 dark:border-dark-background-300 relative flex flex-col items-start border p-6 hyphens-auto sm:rounded-lg">
                                {productDescription}
                            </div>
                        </div>
                    )}
				</div>

				{shouldShowPopularProducts && props.popular_products_for_widget && (
					<div className="mt-4 lg:w-1/3">
						<div className="top-20 lg:sticky">
							<h2 className="my-4 ps-3 text-left text-xl font-bold sm:ps-2">
								Beliebte Produkte
							</h2>
							<div className="bg-background-100 dark:bg-dark-background-300 rounded-none px-3 py-6 shadow-lg sm:rounded-lg sm:px-6">
								<BB_CompactProductsList
									products={props.popular_products_for_widget}
									direction="column"
								/>
							</div>
						</div>
					</div>
				)}
			</div>

			{props.relevant_products && (
				<div className="pt-4">
					<h2 className="my-4 ps-3 text-left text-xl font-bold sm:ps-2">
						Relevante Produkte
					</h2>
                    <div className="bg-background-100 dark:bg-dark-background-300 rounded-none px-3 py-6 shadow-lg sm:rounded-lg sm:px-6">
						<BB_CompactProductsList
							products={props.relevant_products}
							direction={isCompact ? "column" : "row"}
						/>
					</div>
				</div>
			)}
		</Layout>
	);
};

export default memo(ProductPage);
